import type { DonutSegment, TrendDelta } from '../types/analytics.types'

/**
 * Calculates the percentage delta between current and previous period values.
 * `invertDirection` flips the polarity for metrics where lower is better
 * (e.g. settlement time): a decrease then becomes a 'positive' trend.
 *
 * Returns null when there's no meaningful signal:
 * - both periods empty (no activity to compare)
 * - previous=0 (can't compute % growth from zero)
 */
export function calcTrendDelta(current: number, previous: number, invertDirection = false): TrendDelta | null {
  if (!current && !previous) return null
  if (!previous) return null

  const pct = ((current - previous) / Math.abs(previous)) * 100
  const rounded = Math.abs(pct).toFixed(1)

  if (pct === 0) return { value: '0%', direction: 'neutral' }

  const isImprovement = invertDirection ? pct < 0 : pct > 0
  return {
    value: `${pct > 0 ? '+' : '-'}${rounded}%`,
    direction: isImprovement ? 'positive' : 'negative'
  }
}

/**
 * Absolute seconds delta for settlement time. Lower is better, so a decrease
 * is 'positive'. Returns null when there's no meaningful comparison.
 */
export function calcSettlementDelta(current: number, previous: number): TrendDelta | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null
  if (!current && !previous) return null
  if (!previous) return null
  const diff = current - previous
  if (diff === 0) return { value: '0s', direction: 'neutral' }
  return {
    value: `${diff < 0 ? '-' : '+'}${Math.abs(diff).toFixed(1)}s`,
    direction: diff < 0 ? 'positive' : 'negative'
  }
}

/**
 * Formats settlement time in seconds to a human-readable string.
 */
export function formatSettlementTime(seconds: number): string {
  if (!seconds || !Number.isFinite(seconds)) return '0s'
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m ${secs}s`
}

/**
 * Maps payment method type enum to a display-friendly label.
 */
export function mapPaymentMethodDisplayName(methodType: string): string {
  const map: Record<string, string> = {
    INSTANT_BANK_TRANSFER: 'Instant Bank',
    COMMUNITY_INSTANT_BANK_TRANSFER: 'Instant Bank',
    CREDIT_CARD: 'Credit Card',
    DEBIT_CARD: 'Debit Card',
    BANKWIRE: 'Bankwire',
    LOCAL_BANKWIRE: 'Local Bankwire',
    SWIFT_BANKWIRE: 'Swift Bankwire',
    BOLETO: 'Boleto',
    PIX: 'Pix',
    EMONEY: 'E-Money',
    PAGO_EFECTIVO_BANK: 'Pago Efectivo Bank',
    PAGO_EFECTIVO_CASH: 'Pago Efectivo Cash',
    APPLE_PAY: 'Apple Pay',
    GOOGLE_PAY: 'Google Pay'
  }
  return map[methodType] ?? methodType
}

/**
 * Takes a sorted-by-percentage array of items, returns the top N entries
 * plus an aggregated "Other" entry if there are more than N items.
 */
export function buildTopNWithOther<T extends { percentage: number }>(
  items: T[],
  labelKey: keyof T,
  colors: string[],
  n = 4
): DonutSegment[] {
  if (!items.length) return []

  const sorted = [...items].sort((a, b) => b.percentage - a.percentage)
  const top = sorted.slice(0, n)
  const rest = sorted.slice(n)

  const segments: DonutSegment[] = top.map((item, i) => ({
    label: String(item[labelKey]),
    percentage: item.percentage,
    color: colors[i] ?? 'var(--dash-text-faint)'
  }))

  if (rest.length > 0) {
    const otherPct = rest.reduce((sum, item) => sum + item.percentage, 0)
    segments.push({
      label: 'Other',
      percentage: Math.round(otherPct * 10) / 10,
      color: colors[n] ?? 'var(--dash-border)'
    })
  }

  return segments
}
